(() => {
  if (customElements.get('ecomhard-shipping-calculator')) return;

  class EcomhardShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('[data-shipping-form]');
      this.zipInput = this.querySelector('[data-shipping-zip]');
      this.submitButton = this.querySelector('[data-shipping-submit]');
      this.message = this.querySelector('[data-shipping-message]');
      this.ratesContainer = this.querySelector('[data-shipping-rates]');
      this.isCalculating = false;
      this.temporaryLineKey = null;
      this.requestId = 0;
    }

    connectedCallback() {
      if (!this.form || !this.zipInput || !this.submitButton) return;

      this.onInput = this.handleZipInput.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onPageHide = this.handlePageHide.bind(this);

      this.zipInput.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', this.onSubmit);
      window.addEventListener('pagehide', this.onPageHide);

      this.handleZipInput();
    }

    disconnectedCallback() {
      this.zipInput?.removeEventListener('input', this.onInput);
      this.form?.removeEventListener('submit', this.onSubmit);
      window.removeEventListener('pagehide', this.onPageHide);
    }

    get rootUrl() {
      return window.Shopify?.routes?.root || '/';
    }

    log(...args) {
      console.info('[REV][Frete]', ...args);
    }

    handlePageHide() {
      if (this.temporaryLineKey) this.removeTemporaryItem({ keepalive: true, timeout: 1500 });
    }

    handleZipInput() {
      const digits = this.zipInput.value.replace(/\D/g, '').slice(0, 8);
      this.zipInput.value = digits.length > 5
        ? `${digits.slice(0, 5)}-${digits.slice(5)}`
        : digits;

      this.submitButton.disabled = digits.length !== 8 || this.isCalculating;

      if (this.message.classList.contains('is-error')) this.clearMessage();
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.isCalculating) return;

      const zip = this.zipInput.value.replace(/\D/g, '');
      if (zip.length !== 8) return;

      const variantId = this.getVariantId();
      if (!variantId) {
        this.showError('Não há uma variante disponível para calcular o frete.');
        return;
      }

      const quantity = this.getQuantity();
      const currentRequestId = ++this.requestId;

      this.startLoading();
      this.clearMessage();
      this.clearRates();

      try {
        this.log('Iniciando cálculo', { zip, variantId, quantity });

        /*
         * CEP e carrinho são preparados em paralelo para reduzir o tempo total.
         * A consulta ao ViaCEP é apenas um enriquecimento: se falhar, o cálculo
         * continua usando somente CEP + Brasil.
         */
        const addressPromise = this.resolveAddress(zip);
        const temporaryLine = await this.addTemporaryItem(variantId, quantity);
        this.temporaryLineKey = temporaryLine?.key || null;

        if (!this.temporaryLineKey) throw new Error('TEMP_ITEM_NOT_FOUND');

        await this.confirmTemporaryItem(this.temporaryLineKey);

        const resolvedAddress = await addressPromise;
        const addressCandidates = this.buildAddressCandidates(zip, resolvedAddress);

        this.log('Endereços candidatos', addressCandidates);

        const rates = await this.fetchRatesUsingCandidates(addressCandidates);

        if (currentRequestId !== this.requestId) return;
        this.renderRates(rates);
      } catch (error) {
        console.error('[REV][Frete] Erro no cálculo:', error);
        if (currentRequestId === this.requestId) {
          this.clearRates();
          this.showError(this.getFriendlyError(error));
        }
      } finally {
        /*
         * O resultado é liberado ANTES da limpeza do item temporário.
         * Assim um app de carrinho lento nunca mantém o bloco preso em “Calculando...”.
         */
        this.hideLoadingOverlay();

        await this.removeTemporaryItem({ timeout: 3500 });

        this.isCalculating = false;
        this.removeAttribute('aria-busy');
        this.handleZipInput();
      }
    }

    getVariantId() {
      const productInfo = this.closest('product-info');
      const variantInput = productInfo?.querySelector('product-form input.product-variant-id[name="id"]');
      const selectedVariantId = variantInput?.value?.trim();
      const fallbackVariantId = this.dataset.defaultVariantId?.trim();

      return selectedVariantId || fallbackVariantId || '';
    }

    getQuantity() {
      const productInfo = this.closest('product-info');
      const quantityInput = productInfo?.querySelector('product-form input[name="quantity"]');
      return Math.max(1, parseInt(quantityInput?.value || '1', 10) || 1);
    }

    async resolveAddress(zip) {
      const fallback = { zip, country: 'Brazil', countryCode: 'BR', province: '', provinceCode: '', city: '' };

      try {
        const response = await this.fetchWithTimeout(
          `https://viacep.com.br/ws/${encodeURIComponent(zip)}/json/`,
          { headers: { Accept: 'application/json' }, cache: 'no-store' },
          2500
        );

        if (!response.ok) return fallback;

        const data = await response.json().catch(() => null);
        if (!data) return fallback;
        if (data.erro) throw new Error('INVALID_ZIP');

        return {
          zip,
          country: 'Brazil',
          countryCode: 'BR',
          province: this.getBrazilianProvinceName(data.uf) || '',
          provinceCode: String(data.uf || '').toUpperCase(),
          city: data.localidade || ''
        };
      } catch (error) {
        if (error?.message === 'INVALID_ZIP') throw error;
        this.log('ViaCEP indisponível; seguindo somente com o CEP.');
        return fallback;
      }
    }

    getBrazilianProvinceName(uf) {
      const provinces = {
        AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia',
        CE: 'Ceará', DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás',
        MA: 'Maranhão', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais',
        PA: 'Pará', PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí',
        RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul',
        RO: 'Rondônia', RR: 'Roraima', SC: 'Santa Catarina', SP: 'São Paulo',
        SE: 'Sergipe', TO: 'Tocantins'
      };

      return provinces[String(uf || '').toUpperCase()] || '';
    }

    buildAddressCandidates(zip, resolved) {
      const candidates = [];
      const push = (address) => {
        const key = JSON.stringify(address);
        if (!candidates.some((item) => JSON.stringify(item) === key)) candidates.push(address);
      };

      /* Preferimos o endereço completo no formato que a Shopify usa no Brasil. */
      if (resolved?.province || resolved?.city) {
        push({
          zip,
          country: 'Brazil',
          province: resolved.province || '',
          city: resolved.city || ''
        });
      }

      /* Alguns serviços de transportadora esperam UF em vez do nome do estado. */
      if (resolved?.provinceCode) {
        push({
          zip,
          country: 'Brazil',
          province: resolved.provinceCode,
          city: resolved.city || ''
        });

        push({
          zip,
          country: 'BR',
          province: resolved.provinceCode,
          city: resolved.city || ''
        });
      }

      /* Fallback mais permissivo: a Shopify resolve a região pelo CEP. */
      push({ zip, country: 'Brazil', province: '', city: '' });
      push({ zip, country: 'BR', province: '', city: '' });

      return candidates;
    }

    async addTemporaryItem(variantId, quantity) {
      const token = `${this.dataset.blockId || 'pdp'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const item = {
        id: Number(variantId),
        quantity,
        properties: {
          _ecomhard_shipping_preview: token
        }
      };

      const response = await this.fetchWithTimeout(
        `${this.rootUrl}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items: [item] }),
          cache: 'no-store'
        },
        8000
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.description || data?.message || `ADD_FAILED_${response.status}`);
      }

      const addedItem = data?.items?.[0] || data;
      this.log('Item temporário adicionado', addedItem?.key || addedItem?.id);
      return addedItem;
    }

    async confirmTemporaryItem(lineKey) {
      const response = await this.fetchWithTimeout(
        `${this.rootUrl}cart.js?_=${Date.now()}`,
        { headers: { Accept: 'application/json' }, cache: 'no-store' },
        5000
      );

      if (!response.ok) throw new Error(`CART_CONFIRM_${response.status}`);

      const cart = await response.json().catch(() => null);
      const exists = Array.isArray(cart?.items) && cart.items.some((item) => item.key === lineKey);

      if (!exists) throw new Error('TEMP_ITEM_NOT_FOUND');
      this.log('Item temporário confirmado no carrinho.');
    }

    async removeTemporaryItem({ keepalive = false, timeout = 3500 } = {}) {
      if (!this.temporaryLineKey) return;

      const lineKey = this.temporaryLineKey;
      this.temporaryLineKey = null;

      try {
        const response = await this.fetchWithTimeout(
          `${this.rootUrl}cart/change.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ id: lineKey, quantity: 0 }),
            keepalive,
            cache: 'no-store'
          },
          timeout
        );

        if (!response.ok) throw new Error(`REMOVE_FAILED_${response.status}`);
        this.log('Item temporário removido.');
      } catch (error) {
        console.warn('[REV][Frete] Não foi possível remover o item temporário na primeira tentativa:', error);

        /* Última tentativa curta, sem segurar a interface. */
        try {
          fetch(`${this.rootUrl}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ id: lineKey, quantity: 0 }),
            keepalive: true
          }).catch(() => {});
        } catch (_) {}
      }
    }

    buildShippingParams(address) {
      const params = new URLSearchParams();
      params.set('shipping_address[zip]', address.zip);
      params.set('shipping_address[country]', address.country);
      if (address.province) params.set('shipping_address[province]', address.province);
      if (address.city) params.set('shipping_address[city]', address.city);
      params.set('_', String(Date.now()));
      return params;
    }

    async fetchRatesUsingCandidates(candidates) {
      let lastError = null;
      let sawValidResponse = false;

      for (const address of candidates) {
        try {
          const rates = await this.fetchShippingRates(address);
          sawValidResponse = true;

          if (Array.isArray(rates) && rates.length) return rates;
        } catch (error) {
          lastError = error;
          this.log('Tentativa de endereço sem tarifa', address, error?.message || error);
        }
      }

      if (sawValidResponse) return [];
      throw lastError || new Error('RATE_REQUEST_FAILED');
    }

    async fetchShippingRates(address) {
      /*
       * O fluxo prepare + async é o recomendado pela Shopify e também retorna
       * metadados de entrega mais completos (delivery_days / delivery_range)
       * quando a tarifa disponibiliza prazo. Isso é essencial para exibir o
       * tempo de trânsito configurado no admin.
       */
      try {
        const asyncRates = await this.fetchAsyncRates(address);
        if (Array.isArray(asyncRates) && asyncRates.length) return asyncRates;
      } catch (error) {
        this.log('Fluxo assíncrono indisponível; tentando endpoint direto.', error?.message || error);
      }

      /*
       * Fallback: mantém compatibilidade com lojas/transportadoras que respondem
       * melhor pelo endpoint direto. Esse retorno pode ter menos dados de prazo.
       */
      const directResult = await this.fetchDirectRates(address);
      if (directResult.state === 'ready') return directResult.rates;

      return [];
    }

    async fetchDirectRates(address) {
      const query = this.buildShippingParams(address).toString();
      const url = `${this.rootUrl}cart/shipping_rates.json?${query}`;

      try {
        const response = await this.fetchWithTimeout(
          url,
          { method: 'GET', headers: { Accept: 'application/json' }, cache: 'no-store' },
          12000
        );

        const payload = await response.json().catch(() => null);
        this.log('shipping_rates.json', response.status, payload);

        if (response.ok && payload && Array.isArray(payload.shipping_rates)) {
          return { state: 'ready', rates: payload.shipping_rates };
        }

        if (response.status === 422) {
          return { state: 'not-ready', rates: [] };
        }

        if (response.status === 429 || response.status >= 500) {
          return { state: 'not-ready', rates: [] };
        }

        throw new Error(`DIRECT_RATES_${response.status}`);
      } catch (error) {
        if (error?.name === 'AbortError') {
          this.log('shipping_rates.json demorou; migrando para fluxo assíncrono.');
          return { state: 'not-ready', rates: [] };
        }
        throw error;
      }
    }

    async fetchAsyncRates(address) {
      const query = this.buildShippingParams(address).toString();

      const prepareResponse = await this.fetchWithTimeout(
        `${this.rootUrl}cart/prepare_shipping_rates.json?${query}`,
        {
          method: 'POST',
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        },
        8000
      );

      this.log('prepare_shipping_rates', prepareResponse.status);

      if (!prepareResponse.ok && prepareResponse.status !== 422) {
        throw new Error(`RATE_PREPARE_${prepareResponse.status}`);
      }

      const maxAttempts = 18;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (attempt > 0) await this.wait(500);

        const response = await this.fetchWithTimeout(
          `${this.rootUrl}cart/async_shipping_rates.json?${query}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            cache: 'no-store'
          },
          5000
        );

        if (response.status === 422) throw new Error('NO_RATES');
        if (!response.ok) continue;

        const payload = await response.json().catch(() => null);
        this.log(`async_shipping_rates ${attempt + 1}/${maxAttempts}`, payload);

        if (payload && Array.isArray(payload.shipping_rates)) {
          return payload.shipping_rates;
        }
      }

      throw new Error('RATE_TIMEOUT');
    }

    renderRates(rates) {
      this.ratesContainer.innerHTML = '';

      if (!Array.isArray(rates) || rates.length === 0) {
        this.ratesContainer.hidden = true;
        this.showError('Não encontramos opções de frete para este CEP.');
        return;
      }

      const fragment = document.createDocumentFragment();

      rates.forEach((rate) => {
        const item = document.createElement('div');
        item.className = 'ecomhard-shipping-calculator__rate';

        const main = document.createElement('div');
        main.className = 'ecomhard-shipping-calculator__rate-main';

        const name = document.createElement('span');
        name.className = 'ecomhard-shipping-calculator__rate-name';
        name.textContent = rate.presentment_name || rate.name || rate.code || 'Frete';

        const price = document.createElement('span');
        price.className = 'ecomhard-shipping-calculator__rate-price';
        price.textContent = this.formatPrice(rate.price, rate.currency);

        main.append(name, price);
        item.appendChild(main);

        const eta = this.getDeliveryText(rate);
        if (eta) {
          const time = document.createElement('p');
          time.className = 'ecomhard-shipping-calculator__rate-time';
          time.textContent = eta;
          item.appendChild(time);
        }

        fragment.appendChild(item);
      });

      this.ratesContainer.appendChild(fragment);
      this.ratesContainer.hidden = false;
      this.clearMessage();
    }

    getDeliveryText(rate) {
      const deliveryDays = Array.isArray(rate.delivery_days)
        ? rate.delivery_days
        : (Number.isFinite(Number(rate.delivery_days)) ? [rate.delivery_days] : []);

      if (deliveryDays.length) {
        const days = deliveryDays
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value));

        if (days.length >= 2) {
          const min = Math.min(...days);
          const max = Math.max(...days);
          if (min === max) return `Entrega em ${max} ${max === 1 ? 'dia útil' : 'dias úteis'}`;
          if (min === 0) return `Entrega em até ${max} ${max === 1 ? 'dia útil' : 'dias úteis'}`;
          return `Entrega em ${min} a ${max} dias úteis`;
        }

        if (days.length === 1) {
          return `Entrega em ${days[0]} ${days[0] === 1 ? 'dia útil' : 'dias úteis'}`;
        }
      }

      if (Array.isArray(rate.delivery_range) && rate.delivery_range.length >= 2) {
        const start = this.formatDate(rate.delivery_range[0]);
        const end = this.formatDate(rate.delivery_range[1]);
        if (start && end) return start === end ? `Entrega prevista para ${end}` : `Entrega entre ${start} e ${end}`;
      }

      if (rate.delivery_range && typeof rate.delivery_range === 'object') {
        const start = this.formatDate(rate.delivery_range.minimum || rate.delivery_range.min || rate.delivery_range.start);
        const end = this.formatDate(rate.delivery_range.maximum || rate.delivery_range.max || rate.delivery_range.end);
        if (start && end) return start === end ? `Entrega prevista para ${end}` : `Entrega entre ${start} e ${end}`;
      }

      if (rate.delivery_date) {
        const date = this.formatDate(rate.delivery_date);
        if (date) return `Entrega prevista para ${date}`;
      }

      if (rate.estimated_time_in_transit != null) {
        const transit = rate.estimated_time_in_transit;
        if (typeof transit === 'number' || /^\d+$/.test(String(transit))) {
          const days = Number(transit);
          return `Entrega em ${days} ${days === 1 ? 'dia útil' : 'dias úteis'}`;
        }
        if (typeof transit === 'string' && transit.trim()) return transit.trim();
      }

      if (rate.description) return rate.description;

      const sourceText = `${rate.presentment_name || ''} ${rate.name || ''} ${rate.code || ''}`;
      const rangeMatch = sourceText.match(/(\d+)\s*(?:a|até|-)\s*(\d+)\s*dias?\s*(?:úteis)?/i);
      if (rangeMatch) return `Entrega em ${rangeMatch[1]} a ${rangeMatch[2]} dias úteis`;

      const dayMatch = sourceText.match(/(\d+)\s*dias?\s*(?:úteis)?/i);
      if (dayMatch) return `Entrega em ${dayMatch[1]} ${dayMatch[1] === '1' ? 'dia útil' : 'dias úteis'}`;

      return 'Prazo informado no checkout';
    }

    formatPrice(value, currency) {
      const amount = Number.parseFloat(value || '0');
      if (!Number.isFinite(amount) || amount === 0) return 'Grátis';

      const activeCurrency = currency || window.Shopify?.currency?.active || 'BRL';

      try {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: activeCurrency
        }).format(amount);
      } catch (_) {
        return `R$ ${amount.toFixed(2).replace('.', ',')}`;
      }
    }

    formatDate(value) {
      if (!value) return '';
      const date = new Date(`${value}T12:00:00`);
      if (Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(date);
    }

    getFriendlyError(error) {
      const message = String(error?.message || '');

      if (message === 'INVALID_ZIP') return 'CEP inválido. Confira e tente novamente.';
      if (message === 'NO_RATES') return 'Não encontramos opções de frete para este CEP.';
      if (message === 'RATE_TIMEOUT' || error?.name === 'AbortError') {
        return 'A transportadora demorou para responder. Tente calcular novamente.';
      }
      if (/sold out|estoque|inventory|unavailable/i.test(message)) {
        return 'Este produto não está disponível para cálculo de frete.';
      }
      if (/ADD_FAILED|TEMP_ITEM|CART_CONFIRM/i.test(message)) {
        return 'Não foi possível preparar o produto para o cálculo. Tente novamente.';
      }

      return 'Não foi possível calcular o frete agora. Tente novamente.';
    }

    showError(text) {
      this.message.textContent = text;
      this.message.classList.add('is-error');
    }

    clearMessage() {
      this.message.textContent = '';
      this.message.classList.remove('is-error');
    }

    clearRates() {
      this.ratesContainer.innerHTML = '';
      this.ratesContainer.hidden = true;
    }

    startLoading() {
      this.isCalculating = true;
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      this.submitButton.disabled = true;
    }

    hideLoadingOverlay() {
      this.classList.remove('is-loading');
    }

    fetchWithTimeout(url, options = {}, timeout = 8000) {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), timeout);

      return fetch(url, { ...options, signal: controller.signal })
        .finally(() => window.clearTimeout(timer));
    }

    wait(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    }
  }

  customElements.define('ecomhard-shipping-calculator', EcomhardShippingCalculator);
})();
